// src/components/blogs/EditBlog.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories } from "../redux/slices/categorySlice";
import {
  fetchBlogById,
  updateBlog,
  clearBlogMessages,
} from "../redux/slices/blogslice";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useParams, useNavigate } from "react-router-dom";

const EditBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { categories } = useSelector((state) => state.categories);
  const { singleBlog, success, error, loading } = useSelector(
    (state) => state.blogs
  );

  const [formData, setFormData] = useState({
    permalink: "",
    blogTitle: "",
    blogDescription: "",
    category: "",
    imageAltText: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
  });
  const [blogImage, setBlogImage] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // ✅ Fetch categories & blog
  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchBlogById(id)).finally(() => {
      setInitialLoading(false);
    });
  }, [dispatch, id]);

  // ✅ Prefill when blog data is available
  useEffect(() => {
    if (singleBlog && Object.keys(singleBlog).length > 0) {
      setFormData({
        permalink: singleBlog.permalink || "",
        blogTitle: singleBlog.blogTitle || "",
        blogDescription: singleBlog.blogDescription || "",
        category: singleBlog.category?._id || "",
        imageAltText: singleBlog.imageAltText || "",
        metaTitle: singleBlog.metaTitle || "",
        metaDescription: singleBlog.metaDescription || "",
        metaKeywords: singleBlog.metaKeywords || "",
      });
    }
  }, [singleBlog, id]);

  // ✅ Handle success/error messages
  useEffect(() => {
    if (success || error) {
      setTimeout(() => {
        dispatch(clearBlogMessages());
      }, 3000);

      if (success) {
        navigate("/all-blogs");
      }
    }
  }, [success, error, dispatch, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    setBlogImage(e.target.files[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });
    if (blogImage) {
      data.append("blogImage", blogImage);
    }
    dispatch(updateBlog({ id, formData: data }));
  };

  // ✅ Show loader until first fetch complete
  if (initialLoading) {
    return <div className="p-4">Loading blog details...</div>;
  }

  return (
    <div className="container mt-4">
      <div className="card shadow">
        <div className="card-header bg-warning text-white">
          <h6 className="mb-0 text-white">Edit Blog</h6>
        </div>
        <div className="card-body">
          {success && <div className="alert alert-success">{success}</div>}
          {error && <div className="alert alert-danger">{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* Permalink */}
            <div className="mb-3">
              <label className="form-label">Permalink *</label>
              <input
                type="text"
                name="permalink"
                value={formData.permalink}
                onChange={handleChange}
                className="form-control"
                placeholder="unique-blog-url"
                required
              />
            </div>

            {/* Blog Title */}
            <div className="mb-3">
              <label className="form-label">Blog Title *</label>
              <input
                type="text"
                name="blogTitle"
                value={formData.blogTitle}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter blog title"
                required
              />
            </div>

            {/* Blog Image + Alt Text + Category */}
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Blog Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="form-control"
                />
                {singleBlog?.blogImage && (
                  <img
                    src={singleBlog.blogImage}
                    alt={singleBlog.imageAltText}
                    className="mt-2"
                    style={{ width: "100px" }}
                  />
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">Image Alt Text</label>
                <input
                  type="text"
                  name="imageAltText"
                  value={formData.imageAltText}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Describe the image"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="">Select Category</option>
                  {categories?.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.categoryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Blog Description */}
            <div className="mb-3">
              <label className="form-label">Blog Description *</label>
              <CKEditor
                editor={ClassicEditor}
                data={formData.blogDescription}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setFormData({ ...formData, blogDescription: data });
                }}
              />
            </div>

            {/* Meta Title */}
            <div className="mb-3">
              <label className="form-label">Meta Title</label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            {/* Meta Description */}
            <div className="mb-3">
              <label className="form-label">Meta Description</label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                className="form-control"
                rows="2"
              ></textarea>
            </div>

            {/* Meta Keywords */}
            <div className="mb-3">
              <label className="form-label">Meta Keywords</label>
              <input
                type="text"
                name="metaKeywords"
                value={formData.metaKeywords}
                onChange={handleChange}
                className="form-control"
                placeholder="keyword1, keyword2"
              />
            </div>

            <button
              type="submit"
              className="btn btn-warning"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Blog"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditBlog;
